import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import moment from 'moment-timezone';
import { BehaviorSubject, Subscription, filter } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TimeZoneFormatEnum } from '../../../Misc/enums/time-zone-format.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { SharedReportListItemModel } from '../../models/shared-report-list-item.model';
import { SharedReportSearchService } from '../../services/shared-report-search.service';
import { SharedReportService } from '../../services/shared-report.service';
import { SharedReportEditDialogComponent } from '../SharedReportEditDialog/shared-report-edit-dialog.component';
import { SharedReportInviteDialogComponent } from '../SharedReportInviteDialog/shared-report-invite-dialog.component';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

@Component({
    selector: 'agpr-shared-report-card',
    templateUrl: './shared-report-card.component.html',
    styleUrls: ['./shared-report-card.component.scss'],
    standalone: false
})
export class SharedReportCardComponent implements OnInit, OnDestroy {
    @Input() id: string;
    @Input() sharedReportListItem: SharedReportListItemModel;
    @Input() selectedResults: SharedReportListItemModel[];
    @Output() selectionChanged: EventEmitter<any> = new EventEmitter();

    public preferences: PreferencesModel;
    focused: boolean = false;
    daysBeforeExpiration: number;
    readonly buttonTypes = ButtonTypesEnum;
    public readonly defaultTimeFormat = TimeZoneFormatEnum.timeZoneFormat;
    public showGlobalLoading: boolean = false;

    sharedReportsUrl = environment.sharedReportsUrl;

    private subscriptions: Subscription[] = [];

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private sharedReportsService: SharedReportService,
        private sharedReportsSearchService: SharedReportSearchService,
        private coreDialogService: CoreDialogService
    ) { }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => this.preferences = prefs
            ),
            this.sharedReportsSearchService.getFocusedSharedReport()?.subscribe(focusedSharedReport => {
                if (focusedSharedReport) {
                    this.focused = focusedSharedReport.id === this.sharedReportListItem.id;
                }
            })
        );
        this.daysBeforeExpiration = this.calculateDaysBeforeExpiration();
    }

    /**
     * click the check box
     */
    checkedStateChange(): void {
        this.selectionChanged.emit(this.selectedResults);
    }

    focusedChanged(event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.sharedReportsSearchService.setFocusedSharedReport(this.sharedReportListItem);
    }

    /**
     * share the shared report
     */
    shareReport(): void {
        this.sharedReportsService.getReport(this.sharedReportListItem.id).subscribe(
            report => {
                this.coreDialogService.open(SharedReportInviteDialogComponent, {
                    inputs: {
                        sharedReport: report
                    }
                });
            }
        );
    }

    /**
     * delete the shared report
     */
    deleteReport(): void {
        this.sharedReportsService.deleteSharedReports([this.sharedReportListItem]);
    }

    editReport(): void {
        this.showGlobalLoading = true;
        if (!this.focused) {
            this.sharedReportsSearchService.setFocusedSharedReport(this.sharedReportListItem);
        }
        this.sharedReportsService.getReport(this.sharedReportListItem.id).subscribe(
            report => {
                this.showGlobalLoading = false;
                const chartDisplayElement = report.charts[0]?.displayElement as ChartDisplayElementModel;
                if (chartDisplayElement && !chartDisplayElement.originalReportBlockId) {
                    const confirmDialogModel: ConfirmDialogModel = {
                        inputs: {
                            heading: 'Reports.sharedReportEditDialog',
                            showCancel: false,
                            okLabel: 'Reports.sharedReportEditDialog.acknowledge',
                            messageHtml: 'Reports.sharedReportEditDialog.notEditableMessage'
                        },
                        outputs: {
                            dialogClose: () => {}
                        }
                    };
                    this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
                } else {
                    const animationTrigger = new BehaviorSubject(
                        new FullProfileAnimationModel(
                            FullProfileDialogAnimationsEnum.slideInOutAnimation,
                            OverlayAnimationStateEnum.open
                        )
                    );
                    this.coreDialogService.open(SharedReportEditDialogComponent, {
                        inputs: {
                            animationTrigger: animationTrigger,
                            sharedReport: report
                        },
                        outputs: {
                            dialogClose: () => {}
                        }
                    });
                }
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * calculate the number of days before expiration
     * @returns number of days before expiration
     */
    private calculateDaysBeforeExpiration(): number {
        return moment(this.sharedReportListItem.expiryDate).diff(new Date(), 'days');
    }

    onKeydown(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if ((event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) && target.classList.contains('card')) {
            this.focusedChanged();
        }
    }
}
